/**
 * Baccarat (Punto Banco) — server-authoritative rules engine (2–6 players against the house, session game with chips).
 *
 * Rules implemented (toggles live in {@link BaccaratConfig}):
 *  - An eight-deck shoe, reshuffled when fewer than a deck's worth of cards remains. Card values: ace 1, 2–9 face value,
 *    tens and court cards 0. A hand's total is the sum of its cards modulo 10.
 *  - Each player bets on PLAYER, BANKER or TIE, in seat order, or sits the hand out.
 *  - Two cards each are dealt to the Player and Banker hands (Player first). A total of 8 or 9 on either hand is a natural
 *    and ends the deal. Otherwise the Player hand draws a third card on 0–5 and stands on 6–7. The Banker hand then
 *    follows the standard drawing table (see {@link baccaratBankerDraws}).
 *  - The higher total wins; equal totals are a tie. Winning Player bets pay 1:1, winning Banker bets pay 1:1 less the
 *    `commission` (5%), winning Tie bets pay `tiePays`:1. On a tie, Player and Banker bets are returned.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export type BaccaratSide = 'PLAYER' | 'BANKER' | 'TIE';

export interface BaccaratConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  decks: number;
  /** Percentage kept from a winning Banker bet. */
  commission: number;
  /** A winning Tie bet pays this many times the stake. */
  tiePays: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type BaccaratPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'HAND_END';
export type BaccaratSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';

export interface BaccaratSeat {
  playerId: string;
  chips: number;
  status: BaccaratSeatStatus;
  side: BaccaratSide | null;
  bet: number;
  /** What came back at the end of the hand (stake plus winnings, the stake on a push, or 0). */
  payout: number;
}

export interface BaccaratResult {
  handNo: number;
  winner: BaccaratSide;
  player: CardId[];
  banker: CardId[];
  playerTotal: number;
  bankerTotal: number;
  natural: boolean;
  seats: { seat: number; side: BaccaratSide; bet: number; payout: number; delta: number }[];
}

export interface BaccaratState {
  config: BaccaratConfig;
  seats: (BaccaratSeat | null)[];
  phase: BaccaratPhase;
  handNo: number;
  /** Seat to bet. */
  turn: number;
  shoe: CardId[];
  player: CardId[];
  banker: CardId[];
  winner: BaccaratSide | null;
  lastResult: BaccaratResult | null;
  handsPlayed: number;
}

export type BaccaratAction = { type: 'bet'; side: BaccaratSide; amount: number } | { type: 'sitOut' };

export type BaccaratLegal = { kind: 'bet'; min: number; max: number; canSitOut: boolean } | { kind: 'none' };

export interface BaccaratView {
  gameType: 'BACCARAT';
  config: BaccaratConfig;
  seat: number | null;
  phase: BaccaratPhase;
  handNo: number;
  turn: number;
  player: CardId[];
  banker: CardId[];
  playerTotal: number;
  bankerTotal: number;
  winner: BaccaratSide | null;
  shoeCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: BaccaratSeatStatus; side: BaccaratSide | null; bet: number; payout: number } | null)[];
  lastResult: BaccaratResult | null;
}

const DEFAULTS: BaccaratConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  decks: 8,
  commission: 5,
  tiePays: 8,
  startDelayMs: 2000,
  dealDelayMs: 900,
  handEndDelayMs: 4500,
};

/** Baccarat card value: ace 1, 2–9 face value, 10 and court cards 0. */
export const baccaratValue = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r >= 10 ? 0 : r;
};

/** A hand's total modulo 10. */
export const baccaratTotal = (cards: readonly CardId[]): number => cards.reduce((n, c) => n + baccaratValue(c), 0) % 10;

/**
 * Whether the Banker draws a third card. `playerThird` is the value of the Player's third card, or null when the
 * Player stood on two cards (then the Banker draws on 0–5).
 */
export function baccaratBankerDraws(bankerTotal: number, playerThird: number | null): boolean {
  if (playerThird === null) return bankerTotal <= 5;
  switch (bankerTotal) {
    case 0:
    case 1:
    case 2:
      return true;
    case 3:
      return playerThird !== 8;
    case 4:
      return playerThird >= 2 && playerThird <= 7;
    case 5:
      return playerThird >= 4 && playerThird <= 7;
    case 6:
      return playerThird === 6 || playerThird === 7;
    default:
      return false;
  }
}

/** Who is dealt the next card (or null when the coup is complete). */
export function baccaratNextDeal(player: readonly CardId[], banker: readonly CardId[]): 'PLAYER' | 'BANKER' | null {
  const n = player.length + banker.length;
  if (n < 4) return n % 2 === 0 ? 'PLAYER' : 'BANKER';
  const pt = baccaratTotal(player.slice(0, 2));
  const bt = baccaratTotal(banker.slice(0, 2));
  if (n === 4) {
    if (pt >= 8 || bt >= 8) return null;
    if (pt <= 5) return 'PLAYER';
    return bt <= 5 ? 'BANKER' : null;
  }
  if (n === 5 && player.length === 3) return baccaratBankerDraws(bt, baccaratValue(player[2]!)) ? 'BANKER' : null;
  return null;
}

function seatsWhere(s: BaccaratState, pred: (p: BaccaratSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: BaccaratState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function advanceBetting(s: BaccaratState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function startHand(s: BaccaratState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  if (s.shoe.length < 52) {
    s.shoe = shuffle(buildDeck({ decks: s.config.decks }), rng);
    events.push({ type: 'shuffle', cards: s.shoe.length });
  }
  s.player = [];
  s.banker = [];
  s.winner = null;
  for (const p of s.seats) {
    if (!p) continue;
    p.side = null;
    p.bet = 0;
    p.payout = 0;
    p.status = p.chips >= s.config.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: BaccaratState, events: EngineEvent[]): void {
  const pt = baccaratTotal(s.player);
  const bt = baccaratTotal(s.banker);
  const winner: BaccaratSide = pt > bt ? 'PLAYER' : bt > pt ? 'BANKER' : 'TIE';
  s.winner = winner;
  const cfg = s.config;
  const seats: BaccaratResult['seats'] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET' || p.side === null) return;
    if (winner === 'TIE' && p.side !== 'TIE') p.payout = p.bet;
    else if (p.side !== winner) p.payout = 0;
    else if (winner === 'PLAYER') p.payout = p.bet * 2;
    else if (winner === 'BANKER') p.payout = p.bet + Math.floor((p.bet * (100 - cfg.commission)) / 100);
    else p.payout = p.bet + p.bet * cfg.tiePays;
    p.chips += p.payout;
    seats.push({ seat, side: p.side, bet: p.bet, payout: p.payout, delta: p.payout - p.bet });
  });
  const natural = s.player.length === 2 && s.banker.length === 2 && (pt >= 8 || bt >= 8);
  s.lastResult = { handNo: s.handNo, winner, player: [...s.player], banker: [...s.banker], playerTotal: pt, bankerTotal: bt, natural, seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, winner, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const baccaratEngine: GameEngine<BaccaratConfig, BaccaratState, BaccaratAction, BaccaratView, BaccaratLegal> = {
  gameType: 'BACCARAT',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('decks', c.decks, 1, 8);
    checkInt('commission', c.commission, 0, 20);
    checkInt('tiePays', c.tiePays, 1, 20);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: BaccaratState = {
      config,
      seats: Array<BaccaratSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      shoe: shuffle(buildDeck({ decks: config.decks }), rng),
      player: [],
      banker: [],
      winner: null,
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', side: null, bet: 0, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bet comes back.
      chips += p.bet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are coming forfeits the bet on the table.
      s.seats[seat] = null;
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut') return { type: 'sitOut' };
    if (raw.type === 'bet' && (raw.side === 'PLAYER' || raw.side === 'BANKER' || raw.side === 'TIE') && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) {
      return { type: 'bet', side: raw.side, amount: raw.amount };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Baccarat action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING', 'INVALID_PHASE', 'Betting is closed');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (action.type === 'sitOut') {
      p.status = 'SIT_OUT';
      events.push({ type: 'sitOut', seat });
      advanceBetting(s, seat);
      return events;
    }
    const max = Math.min(s.config.maxBet, p.chips);
    assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
    p.chips -= action.amount;
    p.bet = action.amount;
    p.side = action.side;
    p.status = 'BET';
    events.push({ type: 'bet', seat, side: action.side, amount: action.amount });
    advanceBetting(s, seat);
    return events;
  },

  getActors(s) {
    return s.phase === 'BETTING' && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    if (s.phase !== 'BETTING' || seat !== s.turn || !s.seats[seat]) return { kind: 'none' };
    return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, s.seats[seat]!.chips), canSitOut: true };
  },

  getTimeoutAction() {
    return { type: 'sitOut' };
  },

  getView(s, seat) {
    return {
      gameType: 'BACCARAT',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      player: [...s.player],
      banker: [...s.banker],
      playerTotal: baccaratTotal(s.player),
      bankerTotal: baccaratTotal(s.banker),
      winner: s.winner,
      shoeCount: s.shoe.length,
      players: s.seats.map((p, i) => (p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, side: p.side, bet: p.bet, payout: p.payout } : null)),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      // One card per step so the table can show the deal as it happens.
      if (s.shoe.length === 0) s.shoe = shuffle(buildDeck({ decks: s.config.decks }), rng);
      const to = baccaratNextDeal(s.player, s.banker);
      if (to === null) {
        settle(s, events);
        return events;
      }
      const card = s.shoe.pop()!;
      (to === 'PLAYER' ? s.player : s.banker).push(card);
      events.push({ type: 'cardDealt', to, card });
      // The coup is over as soon as no further card is due.
      if (baccaratNextDeal(s.player, s.banker) === null) settle(s, events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
